import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { usePlan } from "@/hooks/usePlan";
import { friendlyError } from "@/lib/friendlyError";

const BLOCKED_DOMAINS = [
  "gmail.com", "hotmail.com", "outlook.com", "yahoo.com", "yahoo.co.uk",
  "hotmail.co.uk", "live.com", "icloud.com", "me.com", "aol.com",
  "googlemail.com", "msn.com",
];

interface ReferralEmailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  referralUrl: string;
  referralCode: string;
}

export function ReferralEmailModal({ open, onOpenChange, referralUrl, referralCode }: ReferralEmailModalProps) {
  const { nurseryName } = usePlan();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [note, setNote] = useState("");

  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleEmailBlur = () => {
    if (!email) return;
    const domain = email.split("@")[1]?.toLowerCase();
    if (BLOCKED_DOMAINS.includes(domain)) {
      setEmailError("Please use their work email address.");
    } else {
      setEmailError("");
    }
  };

  const handleSend = async () => {
    if (!email || emailError) return;
    setSending(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-referral-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            recipientEmail: email.trim().toLowerCase(),
            referralUrl,
            referralCode,
          }),
        }
      );

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || "Failed to send invite");
      }

      setSent(true);
    } catch (err: any) {
      toast({ title: "Could not send the invite", description: friendlyError(err), variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      setSent(false);
      setEmail("");
      setEmailError("");
      setNote("");
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="font-heading font-semibold text-xl text-nursly-forest">
            Invite a nursery by email
          </DialogTitle>
        </DialogHeader>

        {sent ? (
          <div className="py-8 text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-nursly-forest-light flex items-center justify-center mx-auto">
              <Check className="h-6 w-6 text-nursly-sage" />
            </div>
            <p className="font-body font-medium text-nursly-forest">Invite sent to {email}</p>
            <Button variant="outline" onClick={() => handleClose(false)}>Close</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="font-body font-medium text-[13px] text-nursly-ink">
                Nursery manager's work email
              </Label>
              <Input
                type="email"
                placeholder="e.g. manager@oakwoodnursery.co.uk"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailError(""); }}
                onBlur={handleEmailBlur}
                autoComplete="off"
              />
              {emailError && <p className="text-xs font-body text-destructive">{emailError}</p>}
              <p className="text-[11px] font-body text-nursly-faint">Please use their work email address.</p>
            </div>


            {/* Email preview */}
            <div className="border-t pt-3" style={{ borderColor: "rgba(13,31,20,0.08)" }}>
              <p className="text-[10px] font-body font-semibold uppercase tracking-wider text-nursly-faint mb-2">Email preview</p>
              <div className="rounded-lg border p-4 text-xs font-body space-y-2" style={{ borderColor: "rgba(13,31,20,0.09)", background: "#F9F6F1" }}>
                <p className="font-medium text-nursly-ink">Subject: {nurseryName || "Your nursery"} thinks you'll want to see this</p>
                <p className="text-nursly-muted">We switched to Nursly and it's taken away the panic of Ofsted turning up unannounced. Every document they'd ask for is ready in one click, ratios watched live, and certificates get flagged before they run out.</p>
                <p className="text-nursly-muted">Two months off if you sign up with my link, and so do I. The free plan costs nothing to try, no card needed.</p>
                <p className="text-nursly-sage break-all">{referralUrl}</p>
              </div>
            </div>

            <Button
              className="w-full"
              onClick={handleSend}
              disabled={sending || !email || !!emailError}
            >
              {sending ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Sending…</> : "Send invite"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
